import React from 'react';
import { cx } from './Primitives';

/* ============================================================================
   Tables (DESIGN-SYSTEM §9).

   Business data is a real <table>: the header and the cells share their
   columns by construction, so nothing floats. The rules the components hold:

   - text is left-aligned; figures right-aligned, in tabular numerals, never
     wrapped — `numeric` does all three;
   - the first and last columns are inset 20px, the same as a panel's heading,
     so a table's left edge and its title's line up;
   - the header is a quiet band in small type; rows are 44px, divided by a
     hairline, with no line under the last;
   - a column that is not essential says at what width it appears
     (`hideBelow`), so a narrow table drops columns by priority instead of
     squeezing every one of them. Widths are the table's own container's, so
     a table beside a side column and a table on its own behave alike.
   ============================================================================ */

/** At what container width a column appears. */
export type ColumnFrom = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const FROM: Record<ColumnFrom, string> = {
    sm: 'hidden @min-[36rem]:table-cell',
    md: 'hidden @min-[45rem]:table-cell',
    lg: 'hidden @min-[56rem]:table-cell',
    xl: 'hidden @min-[66rem]:table-cell',
    '2xl': 'hidden @min-[80rem]:table-cell',
};

const CELL = 'px-3 first:pl-5 last:pr-5';

export const Table = ({
    caption, children, className, ...rest
}: React.TableHTMLAttributes<HTMLTableElement> & { caption: string }) => (
    <div className="@container overflow-x-auto">
        <table {...rest} className={cx('w-full border-collapse text-body [&_tbody_tr:last-child>*]:border-b-0', className)}>
            <caption className="sr-only">{caption}</caption>
            {children}
        </table>
    </div>
);

export const Th = ({
    numeric, hideBelow, className, children, ...rest
}: React.ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean; hideBelow?: ColumnFrom }) => (
    <th
        scope="col"
        {...rest}
        className={cx(
            CELL, 'h-10 bg-card-2 border-b border-separator text-caption font-semibold text-label-3 whitespace-nowrap align-middle',
            numeric ? 'text-right' : 'text-left',
            hideBelow && FROM[hideBelow],
            className,
        )}
    >
        {children}
    </th>
);

export const Td = ({
    numeric, hideBelow, className, children, ...rest
}: React.TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean; hideBelow?: ColumnFrom }) => (
    <td
        {...rest}
        className={cx(
            CELL, 'h-11 py-2 border-b border-separator align-middle',
            numeric && 'text-right num whitespace-nowrap',
            hideBelow && FROM[hideBelow],
            className,
        )}
    >
        {children}
    </td>
);

/** The cell that names its row — a person, an age band — read as the row's header. */
export const RowHead = ({
    className, children, ...rest
}: React.ThHTMLAttributes<HTMLTableCellElement>) => (
    <th
        scope="row"
        {...rest}
        className={cx(CELL, 'h-11 py-2 border-b border-separator align-middle text-left font-semibold text-label', className)}
    >
        {children}
    </th>
);

/** A row somebody can open: a hover shade, and the whole row answers the pointer. */
export const rowClass = (interactive = false) =>
    cx(interactive && 'relative transition-colors duration-100 hover:bg-hover focus-within:bg-hover cursor-pointer');

/**
 * The link that opens a row. Its box is stretched over the whole row, so a
 * press anywhere on the row opens it, while the row stays one tab stop with
 * one accessible name.
 */
export const rowLinkClass =
    'outline-none after:absolute after:inset-0 after:content-[""] focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-focus';
